import { Edit2, FileText, FolderKanban, Package, Plus, RefreshCw, Trash2, X } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import type { Dispatch, FormEvent, SetStateAction } from "react"
import Card from "../components/Card"
import StatusPill from "../components/StatusPill"
import type {
  Brand,
  CatalogItem,
  Client,
  InvoiceSchedulePreviewRow,
  Opportunity,
  ProposalDetail,
  ProposalLineItem,
  ProposalSummary,
} from "../types"

type BrandsResponse = { brands?: Brand[] }
type ClientsResponse = { clients?: Client[] }
type OpportunitiesResponse = { opportunities?: Opportunity[] }
type CatalogResponse = { items?: CatalogItem[] }
type ProposalsResponse = { proposals?: ProposalSummary[] }
type ProposalDetailResponse = { proposal?: ProposalDetail }
type ProposalPreview = {
  title: string
  brand: {
    id: string
    name: string
    legalName: string
    dbaName: string
    termsUrl: string
  }
  client: {
    id: string
    name: string
    company: string
    email: string
    phone: string
    address: string
  } | null
  legalCompanyName: string
  signerName: string
  signerTitle: string
  signerEmail: string
  signerPhone: string
  signerAddress: string
  servicesStartDate: string | null
  billingAnchorDate: string
  billingActivationMode: "automatic" | "manual"
  projectInstallmentMonths: number
  termsUrlSnapshot: string
  commercialTerms: Record<string, unknown>
  generalTermsSnapshot: string
  lineItems: ProposalLineItem[]
  invoiceSchedulePreview: InvoiceSchedulePreviewRow[]
}
type ProposalPreviewResponse = { preview?: ProposalPreview; id?: string }
type SignaturePacketResponse = {
  ok?: boolean
  packet?: {
    id: string
    externalPacketId: string
    provider: string
    status: string
    signerName: string
    signerEmail: string
  }
  error?: string
}
type ProposalDocumentResponse = {
  ok?: boolean
  file?: {
    id: string
    name: string
    category: string
    url: string
  }
  error?: string
}
type ProposalBillingActivationResponse = {
  ok?: boolean
  activation?: {
    scheduleId: string | null
    createdInvoices: string[]
    createdSubscriptions: string[]
    processedEntryIds: string[]
  }
  error?: string
}

const currency = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD" })

const roundMoney = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100

const emptyLineItem = (sortOrder: number): ProposalLineItem => ({
  kind: "project",
  category: "",
  name: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
  amount: 0,
  startDelayMonths: 0,
  durationMonths: null,
  sortOrder,
  metadata: {},
})

const opportunityTone = (stage: Opportunity["stage"]): "success" | "warning" | "danger" | "neutral" => {
  if (stage === "Won") return "success"
  if (stage === "Proposal" || stage === "Qualified") return "warning"
  if (stage === "Lost") return "danger"
  return "neutral"
}

const proposalTone = (status: ProposalSummary["status"]): "success" | "warning" | "danger" | "neutral" => {
  if (status === "Signed") return "success"
  if (status === "Sent" || status === "Viewed") return "warning"
  if (status === "Declined") return "danger"
  return "neutral"
}

const SalesPage = () => {
  const [activeTab, setActiveTab] = useState<"opportunities" | "catalog" | "proposals">("opportunities")
  const [brands, setBrands] = useState<Brand[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [opportunities, setOpportunities] = useState<Opportunity[]>([])
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([])
  const [proposals, setProposals] = useState<ProposalSummary[]>([])
  const [selectedProposalId, setSelectedProposalId] = useState("")
  const [selectedProposal, setSelectedProposal] = useState<ProposalDetail | null>(null)
  const [proposalPreview, setProposalPreview] = useState<ProposalPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [catalogSelectionId, setCatalogSelectionId] = useState("")
  const [opportunityForm, setOpportunityForm] = useState({
    clientId: "",
    brandId: "",
    title: "",
    stage: "Open" as Opportunity["stage"],
    opportunityType: "New" as Opportunity["opportunityType"],
    servicesStartDate: "",
    notes: "",
  })
  const [catalogForm, setCatalogForm] = useState({
    brandId: "",
    category: "",
    itemType: "project" as CatalogItem["itemType"],
    name: "",
    description: "",
    defaultUnitPrice: "0",
    defaultQuantity: "1",
    defaultStartDelayMonths: "0",
    defaultDurationMonths: "",
    billingNote: "",
  })
  const [editingCatalogId, setEditingCatalogId] = useState<string | null>(null)

  const startEditCatalogItem = (item: CatalogItem) => {
    setEditingCatalogId(item.id)
    setCatalogForm({
      brandId: item.brandId || "",
      category: item.category,
      itemType: item.itemType,
      name: item.name,
      description: item.description,
      defaultUnitPrice: String(item.defaultUnitPrice),
      defaultQuantity: String(item.defaultQuantity),
      defaultStartDelayMonths: String(item.defaultStartDelayMonths),
      defaultDurationMonths: item.defaultDurationMonths === null ? "" : String(item.defaultDurationMonths),
      billingNote: item.billingNote,
    })
  }

  const cancelEditCatalogItem = () => {
    setEditingCatalogId(null)
    setCatalogForm({
      brandId: "",
      category: "",
      itemType: "project",
      name: "",
      description: "",
      defaultUnitPrice: "0",
      defaultQuantity: "1",
      defaultStartDelayMonths: "0",
      defaultDurationMonths: "",
      billingNote: "",
    })
  }

  const [editingProposalId, setEditingProposalId] = useState<string | null>(null)

  const startEditProposal = (proposal: ProposalDetail) => {
    setEditingProposalId(proposal.id)
    setProposalForm({
      opportunityId: proposal.opportunityId || "",
      brandId: proposal.brandId,
      clientId: proposal.clientId || "",
      title: proposal.title,
      legalCompanyName: proposal.legalCompanyName || "",
      signerName: proposal.signerName || "",
      signerTitle: proposal.signerTitle || "",
      signerEmail: proposal.signerEmail || "",
      signerPhone: proposal.signerPhone || "",
      signerAddress: proposal.signerAddress || "",
      signatureDate: proposal.signatureDate || "",
      effectiveDate: proposal.effectiveDate || "",
      servicesStartDate: proposal.servicesStartDate || "",
      billingAnchorDate: proposal.billingAnchorDate || "",
      billingActivationMode: proposal.billingActivationMode || "automatic",
      projectInstallmentMonths: String(proposal.projectInstallmentMonths || 1),
      additionalTermsText: proposal.additionalTermsText || "",
      signatureProvider: proposal.signatureProvider || "Signature Provider",
      sourceDocumentName: proposal.sourceDocumentName || "",
    })
    setProposalLineItems(
      proposal.lineItems.length > 0
        ? proposal.lineItems.map((item, index) => ({ ...item, sortOrder: index }))
        : [emptyLineItem(0)],
    )
    setProposalPreview(null)
  }

  const cancelEditProposal = () => {
    setEditingProposalId(null)
    setProposalForm({
      opportunityId: "",
      brandId: "",
      clientId: "",
      title: "",
      legalCompanyName: "",
      signerName: "",
      signerTitle: "",
      signerEmail: "",
      signerPhone: "",
      signerAddress: "",
      signatureDate: "",
      effectiveDate: "",
      servicesStartDate: "",
      billingAnchorDate: "",
      billingActivationMode: "automatic",
      projectInstallmentMonths: "1",
      additionalTermsText: "",
      signatureProvider: "Signature Provider",
      sourceDocumentName: "Current Agreement Template",
    })
    setProposalLineItems([emptyLineItem(0)])
    setProposalPreview(null)
  }

  const [proposalForm, setProposalForm] = useState({
    opportunityId: "",
    brandId: "",
    clientId: "",
    title: "",
    legalCompanyName: "",
    signerName: "",
    signerTitle: "",
    signerEmail: "",
    signerPhone: "",
    signerAddress: "",
    signatureDate: "",
    effectiveDate: "",
    servicesStartDate: "",
    billingAnchorDate: "",
    billingActivationMode: "automatic" as "automatic" | "manual",
    projectInstallmentMonths: "1",
    additionalTermsText: "",
    signatureProvider: "Signature Provider",
    sourceDocumentName: "Current Agreement Template",
  })
  const [proposalLineItems, setProposalLineItems] = useState<ProposalLineItem[]>([emptyLineItem(0)])

  const fetchProposalDetail = async (proposalId: string) => {
    const response = await fetch(`/api/proposals/${proposalId}`)
    const data = (await response.json().catch(() => ({}))) as ProposalDetailResponse
    return data.proposal || null
  }

  const loadWorkspace = async () => {
    setLoading(true)
    setError(null)
    try {
      const [brandsRes, clientsRes, opportunitiesRes, catalogRes, proposalsRes] = await Promise.all([
        fetch("/api/brands"),
        fetch("/api/clients?status=Active"),
        fetch("/api/opportunities"),
        fetch("/api/service-catalog?includeInactive=true"),
        fetch("/api/proposals"),
      ])

      const brandsData = (await brandsRes.json().catch(() => ({}))) as BrandsResponse
      const clientsData = (await clientsRes.json().catch(() => ({}))) as ClientsResponse
      const opportunitiesData = (await opportunitiesRes.json().catch(() => ({}))) as OpportunitiesResponse
      const catalogData = (await catalogRes.json().catch(() => ({}))) as CatalogResponse
      const proposalsData = (await proposalsRes.json().catch(() => ({}))) as ProposalsResponse

      setBrands(brandsData.brands || [])
      setClients(clientsData.clients || [])
      setOpportunities(opportunitiesData.opportunities || [])
      setCatalogItems(catalogData.items || [])
      setProposals(proposalsData.proposals || [])
      if (!selectedProposalId && proposalsData.proposals?.length) {
        setSelectedProposalId(proposalsData.proposals[0].id)
      }
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : "Failed to load sales workspace"
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadWorkspace()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedProposalId) {
      setSelectedProposal(null)
      return
    }

    let active = true
    fetchProposalDetail(selectedProposalId)
      .then((proposal) => {
        if (active) {
          setSelectedProposal(proposal)
        }
      })
      .catch(() => {
        if (active) {
          setSelectedProposal(null)
        }
      })

    return () => {
      active = false
    }
  }, [selectedProposalId])

  useEffect(() => {
    const opportunity = opportunities.find((entry) => entry.id === proposalForm.opportunityId)
    if (!opportunity) return

    setProposalForm((current) => ({
      ...current,
      brandId: current.brandId || opportunity.brandId,
      clientId: current.clientId || opportunity.clientId || "",
      title: current.title || opportunity.title,
      servicesStartDate: current.servicesStartDate || opportunity.servicesStartDate || "",
    }))
  }, [opportunities, proposalForm.opportunityId])

  useEffect(() => {
    if (!proposalForm.clientId) return
    const client = clients.find((c) => c.id === proposalForm.clientId)
    if (!client) return

    setProposalForm((current) => ({
      ...current,
      legalCompanyName: current.legalCompanyName || client.company || client.name || "",
      signerName: current.signerName || client.name || "",
      signerEmail: current.signerEmail || client.email || "",
      signerPhone: current.signerPhone || client.phone || "",
      signerAddress: current.signerAddress || client.address || "",
    }))
  }, [clients, proposalForm.clientId])

  const availableCatalogItems = useMemo(
    () =>
      catalogItems.filter((item) =>
        !proposalForm.brandId || item.brandId === null || item.brandId === proposalForm.brandId,
      ),
    [catalogItems, proposalForm.brandId],
  )

  const proposalTotals = useMemo(() => {
    return proposalLineItems.reduce(
      (totals, item) => {
        if (item.kind === "project") totals.project += item.amount
        if (item.kind === "service") totals.service += item.amount
        totals.overall += item.amount
        return totals
      },
      { project: 0, service: 0, overall: 0 },
    )
  }, [proposalLineItems])

  const setField = <T extends Record<string, string>>(setter: Dispatch<SetStateAction<T>>, field: keyof T, value: string) => {
    setter((current) => ({ ...current, [field]: value }))
  }

  const handleOpportunitySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const response = await fetch("/api/opportunities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: opportunityForm.clientId || null,
          brandId: opportunityForm.brandId,
          title: opportunityForm.title,
          stage: opportunityForm.stage,
          opportunityType: opportunityForm.opportunityType,
          servicesStartDate: opportunityForm.servicesStartDate || null,
          notes: opportunityForm.notes,
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error((data as { error?: string }).error || "Failed to create opportunity")
      }

      setOpportunityForm({
        clientId: "",
        brandId: "",
        title: "",
        stage: "Open",
        opportunityType: "New",
        servicesStartDate: "",
        notes: "",
      })
      setSuccess("Opportunity created.")
      await loadWorkspace()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Failed to create opportunity")
    } finally {
      setSaving(false)
    }
  }

  const handleCatalogSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)
    const payload = {
      brandId: catalogForm.brandId || null,
      category: catalogForm.category,
      itemType: catalogForm.itemType,
      name: catalogForm.name,
      description: catalogForm.description,
      defaultUnitPrice: Number(catalogForm.defaultUnitPrice) || 0,
      defaultQuantity: Number(catalogForm.defaultQuantity) || 1,
      defaultStartDelayMonths: Number(catalogForm.defaultStartDelayMonths) || 0,
      defaultDurationMonths:
        catalogForm.defaultDurationMonths.trim() === ""
          ? null
          : Number(catalogForm.defaultDurationMonths),
      billingNote: catalogForm.billingNote,
    }
    try {
      const url = editingCatalogId ? `/api/service-catalog/${editingCatalogId}` : "/api/service-catalog"
      const method = editingCatalogId ? "PUT" : "POST"
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error((data as { error?: string }).error || `Failed to ${editingCatalogId ? "update" : "create"} catalog item`)
      }

      cancelEditCatalogItem()
      setSuccess(editingCatalogId ? "Catalog item updated." : "Catalog item added.")
      await loadWorkspace()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Failed to save catalog item")
    } finally {
      setSaving(false)
    }
  }

  const addLineItem = () => {
    setProposalLineItems((current) => [...current, emptyLineItem(current.length)])
  }

  const addCatalogItemToProposal = () => {
    const item = availableCatalogItems.find((entry) => entry.id === catalogSelectionId)
    if (!item) return

    setProposalLineItems((current) => [
      ...current,
      {
        serviceCatalogItemId: item.id,
        kind: item.itemType,
        category: item.category,
        name: item.name,
        description: item.description,
        quantity: item.defaultQuantity,
        unitPrice: item.defaultUnitPrice,
        amount: roundMoney(item.defaultUnitPrice * item.defaultQuantity),
        startDelayMonths: item.defaultStartDelayMonths,
        durationMonths: item.defaultDurationMonths,
        sortOrder: current.length,
        metadata: item.metadata,
      },
    ])
    setCatalogSelectionId("")
  }

  const updateLineItem = (
    index: number,
    field: keyof ProposalLineItem,
    value: string,
  ) => {
    setProposalLineItems((current) =>
      current.map((item, itemIndex) => {
        if (itemIndex !== index) return item
        const next = { ...item }

        if (field === "kind") next.kind = value as ProposalLineItem["kind"]
        if (field === "category") next.category = value
        if (field === "name") next.name = value
        if (field === "description") next.description = value
        if (field === "quantity") next.quantity = Math.max(1, Number(value) || 1)
        if (field === "unitPrice") next.unitPrice = roundMoney(Number(value) || 0)
        if (field === "amount") next.amount = roundMoney(Number(value) || 0)
        if (field === "startDelayMonths") next.startDelayMonths = Math.max(0, Number(value) || 0)
        if (field === "durationMonths") {
          next.durationMonths = value.trim() === "" ? null : Math.max(1, Number(value) || 1)
        }

        if (field === "quantity" || field === "unitPrice") {
          next.amount = roundMoney(next.quantity * next.unitPrice)
        }

        if (next.kind === "project") {
          next.durationMonths = null
          next.startDelayMonths = 0
        }

        return next
      }),
    )
  }

  const removeLineItem = (index: number) => {
    setProposalLineItems((current) =>
      current
        .filter((_, itemIndex) => itemIndex !== index)
        .map((item, itemIndex) => ({ ...item, sortOrder: itemIndex })),
    )
  }

  const buildProposalPayload = () => ({
    opportunityId: proposalForm.opportunityId,
    brandId: proposalForm.brandId || undefined,
    clientId: proposalForm.clientId || null,
    title: proposalForm.title,
    legalCompanyName: proposalForm.legalCompanyName,
    signerName: proposalForm.signerName,
    signerTitle: proposalForm.signerTitle,
    signerEmail: proposalForm.signerEmail,
    signerPhone: proposalForm.signerPhone,
    signerAddress: proposalForm.signerAddress,
    signatureDate: proposalForm.signatureDate || undefined,
    effectiveDate: proposalForm.effectiveDate || undefined,
    servicesStartDate: proposalForm.servicesStartDate || undefined,
    billingAnchorDate: proposalForm.billingAnchorDate || undefined,
    billingActivationMode: proposalForm.billingActivationMode,
    projectInstallmentMonths: Number(proposalForm.projectInstallmentMonths) || 1,
    additionalTermsText: proposalForm.additionalTermsText || undefined,
    signatureProvider: proposalForm.signatureProvider,
    sourceDocumentName: proposalForm.sourceDocumentName || undefined,
    lineItems: proposalLineItems
      .filter((item) => item.name.trim())
      .map((item, index) => ({
        serviceCatalogItemId: item.serviceCatalogItemId || null,
        kind: item.kind,
        category: item.category || undefined,
        name: item.name,
        description: item.description || undefined,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        amount: item.amount,
        startDelayMonths: item.startDelayMonths,
        durationMonths: item.kind === "project" ? null : item.durationMonths,
        sortOrder: index,
        metadata: item.metadata || {},
      })),
  })

  const handleProposalPreview = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const response = await fetch("/api/proposals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...buildProposalPayload(), previewOnly: true }),
      })
      const data = (await response.json().catch(() => ({}))) as ProposalPreviewResponse & { error?: string }
      if (!response.ok || !data.preview) {
        throw new Error(data.error || "Failed to preview proposal")
      }
      setProposalPreview(data.preview)
      setSuccess("Preview ready.")
    } catch (previewError) {
      setError(previewError instanceof Error ? previewError.message : "Failed to preview proposal")
    } finally {
      setSaving(false)
    }
  }

  const handleProposalCreate = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      if (editingProposalId) {
        const payload = buildProposalPayload()
        const response = await fetch(`/api/proposals/${editingProposalId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        })
        const data = (await response.json().catch(() => ({}))) as { ok?: boolean; preview?: ProposalPreview; error?: string }
        if (!response.ok) {
          throw new Error(data.error || "Failed to update proposal")
        }
        if (data.preview) setProposalPreview(data.preview as ProposalPreview)
        setSelectedProposalId(editingProposalId)
        setSelectedProposal(await fetchProposalDetail(editingProposalId))
        cancelEditProposal()
        setSuccess("Proposal updated.")
        await loadWorkspace()
      } else {
        const response = await fetch("/api/proposals", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildProposalPayload()),
        })
        const data = (await response.json().catch(() => ({}))) as ProposalPreviewResponse & { error?: string }
        if (!response.ok || !data.preview || !data.id) {
          throw new Error(data.error || "Failed to create proposal")
        }

        setProposalPreview(data.preview)
        setSelectedProposalId(data.id)
        setSuccess("Proposal created.")
        await loadWorkspace()
      }
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Failed to save proposal")
    } finally {
      setSaving(false)
    }
  }

  const handleSendToSignatureProvider = async () => {
    if (!selectedProposalId) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const response = await fetch(`/api/proposals/${selectedProposalId}/signature`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: selectedProposal?.signatureProvider || proposalForm.signatureProvider || "Signature Provider",
        }),
      })
      const data = (await response.json().catch(() => ({}))) as SignaturePacketResponse
      if (!response.ok || !data.packet) {
        throw new Error(data.error || "Failed to create signature packet")
      }
      setSuccess(`Signature packet ${data.packet.id} created for ${data.packet.signerEmail}.`)
      await loadWorkspace()
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "Failed to create signature packet")
    } finally {
      setSaving(false)
    }
  }

  const handleGenerateDocument = async () => {
    if (!selectedProposalId) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const response = await fetch(`/api/proposals/${selectedProposalId}/document`, {
        method: "POST",
      })
      const data = (await response.json().catch(() => ({}))) as ProposalDocumentResponse
      if (!response.ok || !data.file) {
        throw new Error(data.error || "Failed to generate proposal document")
      }
      setSuccess(`Generated ${data.file.name}.`)
      await loadWorkspace()
      setSelectedProposal(await fetchProposalDetail(selectedProposalId))
      window.open(data.file.url, "_blank", "noopener,noreferrer")
    } catch (documentError) {
      setError(documentError instanceof Error ? documentError.message : "Failed to generate proposal document")
    } finally {
      setSaving(false)
    }
  }

  const handleActivateBilling = async () => {
    if (!selectedProposalId) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const response = await fetch(`/api/proposals/${selectedProposalId}/activate-billing`, {
        method: "POST",
      })
      const data = (await response.json().catch(() => ({}))) as ProposalBillingActivationResponse
      if (!response.ok || !data.activation) {
        throw new Error(data.error || "Failed to activate billing")
      }

      const createdCount = data.activation.createdInvoices.length + data.activation.createdSubscriptions.length
      setSuccess(
        createdCount > 0
          ? `Billing activated and ${createdCount} Stripe record${createdCount === 1 ? "" : "s"} created.`
          : "Billing activated. Future schedule entries will process when they come due.",
      )
      await loadWorkspace()
      setSelectedProposal(await fetchProposalDetail(selectedProposalId))
    } catch (activationError) {
      setError(activationError instanceof Error ? activationError.message : "Failed to activate billing")
    } finally {
      setSaving(false)
    }
  }

  const selectedBrand = brands.find((brand) => brand.id === proposalForm.brandId)
  const previewToRender = proposalPreview
  const selectedProposalView = !proposalPreview && selectedProposal ? {
    title: selectedProposal.title,
    brand: {
      id: selectedProposal.brandId,
      name: selectedProposal.brandName,
      legalName: "",
      dbaName: "",
      termsUrl: selectedProposal.termsUrlSnapshot,
    },
    client: selectedProposal.clientId
      ? {
          id: selectedProposal.clientId,
          name: selectedProposal.clientName,
          company: selectedProposal.legalCompanyName,
          email: selectedProposal.signerEmail,
          phone: selectedProposal.signerPhone,
          address: selectedProposal.signerAddress,
        }
      : null,
    legalCompanyName: selectedProposal.legalCompanyName,
    signerName: selectedProposal.signerName,
    signerTitle: selectedProposal.signerTitle,
    signerEmail: selectedProposal.signerEmail,
    signerPhone: selectedProposal.signerPhone,
    signerAddress: selectedProposal.signerAddress,
    servicesStartDate: selectedProposal.servicesStartDate,
    billingAnchorDate: selectedProposal.billingAnchorDate || "",
    billingActivationMode: selectedProposal.billingActivationMode,
    projectInstallmentMonths: selectedProposal.projectInstallmentMonths,
    termsUrlSnapshot: selectedProposal.termsUrlSnapshot,
    commercialTerms: selectedProposal.commercialTerms,
    generalTermsSnapshot: selectedProposal.generalTermsSnapshot,
    lineItems: selectedProposal.lineItems,
    invoiceSchedulePreview: selectedProposal.invoiceSchedulePreview,
  } : null
  const renderedTotals = (previewToRender || selectedProposalView)?.lineItems.reduce(
    (totals, item) => {
      if (item.kind === "project") totals.project += item.amount
      if (item.kind === "service") totals.service += item.amount
      totals.overall += item.amount
      return totals
    },
    { project: 0, service: 0, overall: 0 },
  ) || { project: 0, service: 0, overall: 0 }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-lg font-semibold text-slate-900">Sales workspace</p>
            <p className="text-sm text-stone">
              Manage opportunities, catalog pricing, and proposal previews with invoice schedules.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => loadWorkspace()}
              className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            {selectedBrand && (
              <div className="rounded-full border border-line px-4 py-2 text-xs font-semibold text-stone">
                Terms link: {selectedBrand.termsUrl}
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        {[
          { id: "opportunities", label: "Opportunities", icon: FolderKanban },
          { id: "catalog", label: "Catalog", icon: Package },
          { id: "proposals", label: "Proposals", icon: FileText },
        ].map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                isActive
                  ? "bg-moss text-white shadow-soft"
                  : "border border-line bg-white text-stone hover:bg-slate-50"
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {error && (
        <Card className="border-rose-200 bg-rose-50/70">
          <p className="text-sm font-medium text-rose-700">{error}</p>
        </Card>
      )}
      {success && (
        <Card className="border-emerald-200 bg-emerald-50/70">
          <p className="text-sm font-medium text-emerald-700">{success}</p>
        </Card>
      )}

      {loading ? (
        <Card>
          <p className="text-sm text-stone">Loading sales data...</p>
        </Card>
      ) : null}

      {!loading && activeTab === "opportunities" && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.25fr,0.95fr]">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold text-slate-900">Open pipeline</p>
                <p className="text-sm text-stone">Track new work, upgrades, downgrades, and renewals.</p>
              </div>
              <StatusPill tone="neutral" label={`${opportunities.length} records`} />
            </div>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-stone">
                    <th className="pb-3 pr-4">Opportunity</th>
                    <th className="pb-3 pr-4">Brand</th>
                    <th className="pb-3 pr-4">Client</th>
                    <th className="pb-3 pr-4">Stage</th>
                    <th className="pb-3 pr-4">Type</th>
                    <th className="pb-3">Start</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {opportunities.map((opportunity) => (
                    <tr key={opportunity.id}>
                      <td className="py-3 pr-4">
                        <p className="font-semibold text-slate-900">{opportunity.title}</p>
                        <p className="text-xs text-stone">{opportunity.ownerName || "Unassigned"}</p>
                      </td>
                      <td className="py-3 pr-4 text-stone">{opportunity.brandName}</td>
                      <td className="py-3 pr-4 text-stone">{opportunity.clientName || "Unlinked"}</td>
                      <td className="py-3 pr-4">
                        <StatusPill tone={opportunityTone(opportunity.stage)} label={opportunity.stage} />
                      </td>
                      <td className="py-3 pr-4 text-stone">{opportunity.opportunityType}</td>
                      <td className="py-3 text-stone">{opportunity.servicesStartDate || "TBD"}</td>
                    </tr>
                  ))}
                  {opportunities.length === 0 && (
                    <tr>
                      <td className="py-6 text-stone" colSpan={6}>
                        No opportunities yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <p className="text-lg font-semibold text-slate-900">Create opportunity</p>
            <p className="mt-1 text-sm text-stone">Start with the sales motion, then attach proposals to it.</p>
            <form onSubmit={handleOpportunitySubmit} className="mt-5 space-y-3 text-sm">
              <select
                value={opportunityForm.brandId}
                onChange={(event) => setField(setOpportunityForm, "brandId", event.target.value)}
                required
                className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
              >
                <option value="">Select brand</option>
                {brands.filter((brand) => brand.isActive).map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
              <select
                value={opportunityForm.clientId}
                onChange={(event) => setField(setOpportunityForm, "clientId", event.target.value)}
                className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
              >
                <option value="">Optional client / contact</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={opportunityForm.title}
                onChange={(event) => setField(setOpportunityForm, "title", event.target.value)}
                placeholder="Opportunity title"
                required
                className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
              />
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={opportunityForm.stage}
                  onChange={(event) => setField(setOpportunityForm, "stage", event.target.value)}
                  className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                >
                  {["Open", "Qualified", "Proposal", "Won", "Lost", "On Hold"].map((stage) => (
                    <option key={stage} value={stage}>
                      {stage}
                    </option>
                  ))}
                </select>
                <select
                  value={opportunityForm.opportunityType}
                  onChange={(event) => setField(setOpportunityForm, "opportunityType", event.target.value)}
                  className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                >
                  {["New", "Upgrade", "Downgrade", "Renewal", "CrossSell"].map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <input
                type="date"
                value={opportunityForm.servicesStartDate}
                onChange={(event) => setField(setOpportunityForm, "servicesStartDate", event.target.value)}
                className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
              />
              <textarea
                value={opportunityForm.notes}
                onChange={(event) => setField(setOpportunityForm, "notes", event.target.value)}
                rows={4}
                placeholder="Context, constraints, or packaging notes"
                className="w-full rounded-xl border border-line bg-white px-4 py-3 outline-none focus:border-moss"
              />
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss/90 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                Create opportunity
              </button>
            </form>
          </Card>
        </div>
      )}

      {!loading && activeTab === "catalog" && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr,1fr]">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold text-slate-900">Service catalog</p>
                <p className="text-sm text-stone">Projects and recurring services that feed proposal pricing.</p>
              </div>
              <StatusPill tone="neutral" label={`${catalogItems.length} items`} />
            </div>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-stone">
                    <th className="pb-3 pr-4">Item</th>
                    <th className="pb-3 pr-4">Brand</th>
                    <th className="pb-3 pr-4">Type</th>
                    <th className="pb-3 pr-4">Price</th>
                    <th className="pb-3 pr-4">Delay</th>
                    <th className="pb-3 pr-4">Duration</th>
                    <th className="pb-3 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {catalogItems.map((item) => (
                    <tr key={item.id} className={`${!item.isActive ? "opacity-50" : ""} ${editingCatalogId === item.id ? "bg-moss/5" : ""}`}>
                      <td className="py-3 pr-4">
                        <p className="font-semibold text-slate-900">{item.name}</p>
                        <p className="text-xs text-stone">{item.category}</p>
                      </td>
                      <td className="py-3 pr-4 text-stone">
                        {brands.find((brand) => brand.id === item.brandId)?.name || "All brands"}
                      </td>
                      <td className="py-3 pr-4 text-stone">{item.itemType}</td>
                      <td className="py-3 pr-4 text-stone">
                        {currency(item.defaultUnitPrice)} x {item.defaultQuantity}
                      </td>
                      <td className="py-3 pr-4 text-stone">{item.defaultStartDelayMonths} mo</td>
                      <td className="py-3 pr-4 text-stone">
                        {item.defaultDurationMonths === null ? "Recurring" : `${item.defaultDurationMonths} mo`}
                      </td>
                      <td className="py-3">
                        <button
                          type="button"
                          onClick={() => startEditCatalogItem(item)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-stone transition hover:bg-slate-100 hover:text-moss"
                          title="Edit item"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {catalogItems.length === 0 && (
                    <tr>
                      <td className="py-6 text-stone" colSpan={7}>
                        No catalog items yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold text-slate-900">{editingCatalogId ? "Edit catalog item" : "Add catalog item"}</p>
                <p className="mt-1 text-sm text-stone">Use a blank duration for recurring services that continue until cancelled.</p>
              </div>
              {editingCatalogId && (
                <button type="button" onClick={cancelEditCatalogItem} className="inline-flex h-8 w-8 items-center justify-center rounded-full text-stone transition hover:bg-slate-100 hover:text-rose-600" title="Cancel editing">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <form onSubmit={handleCatalogSubmit} className="mt-5 space-y-3 text-sm">
              <select
                value={catalogForm.brandId}
                onChange={(event) => setField(setCatalogForm, "brandId", event.target.value)}
                className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
              >
                <option value="">All brands</option>
                {brands.filter((brand) => brand.isActive).map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={catalogForm.category}
                  onChange={(event) => setField(setCatalogForm, "category", event.target.value)}
                  placeholder="Category"
                  required
                  className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
                <select
                  value={catalogForm.itemType}
                  onChange={(event) => setField(setCatalogForm, "itemType", event.target.value)}
                  className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                >
                  <option value="project">Project</option>
                  <option value="service">Service</option>
                </select>
              </div>
              <input
                type="text"
                value={catalogForm.name}
                onChange={(event) => setField(setCatalogForm, "name", event.target.value)}
                placeholder="Item name"
                required
                className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
              />
              <textarea
                value={catalogForm.description}
                onChange={(event) => setField(setCatalogForm, "description", event.target.value)}
                rows={3}
                placeholder="Description"
                className="w-full rounded-xl border border-line bg-white px-4 py-3 outline-none focus:border-moss"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={catalogForm.defaultUnitPrice}
                  onChange={(event) => setField(setCatalogForm, "defaultUnitPrice", event.target.value)}
                  placeholder="Unit price"
                  className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={catalogForm.defaultQuantity}
                  onChange={(event) => setField(setCatalogForm, "defaultQuantity", event.target.value)}
                  placeholder="Quantity"
                  className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={catalogForm.defaultStartDelayMonths}
                  onChange={(event) => setField(setCatalogForm, "defaultStartDelayMonths", event.target.value)}
                  placeholder="Delay months"
                  className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={catalogForm.defaultDurationMonths}
                  onChange={(event) => setField(setCatalogForm, "defaultDurationMonths", event.target.value)}
                  placeholder="Duration months (blank = recurring)"
                  className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                />
              </div>
              <textarea
                value={catalogForm.billingNote}
                onChange={(event) => setField(setCatalogForm, "billingNote", event.target.value)}
                rows={2}
                placeholder="Optional billing note"
                className="w-full rounded-xl border border-line bg-white px-4 py-3 outline-none focus:border-moss"
              />
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss/90 disabled:opacity-50"
                >
                  {editingCatalogId ? <Edit2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {editingCatalogId ? "Update item" : "Add item"}
                </button>
                {editingCatalogId && (
                  <button type="button" onClick={cancelEditCatalogItem} className="rounded-full border border-line px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </Card>
        </div>
      )}

      {!loading && activeTab === "proposals" && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr,0.9fr]">
          <div className="min-w-0 space-y-6">
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-slate-900">{editingProposalId ? "Edit proposal" : "Create proposal"}</p>
                  <p className="text-sm text-stone">Preview the invoice schedule before the proposal ever gets sent.</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="rounded-full bg-slate-100 px-4 py-2 text-xs font-semibold text-stone">
                    {currency(proposalTotals.overall)} total
                  </div>
                  {editingProposalId && (
                    <button type="button" onClick={cancelEditProposal} className="inline-flex h-8 w-8 items-center justify-center rounded-full text-stone transition hover:bg-slate-100 hover:text-rose-600" title="Cancel editing">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-6">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <select
                    value={proposalForm.opportunityId}
                    onChange={(event) => setField(setProposalForm, "opportunityId", event.target.value)}
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  >
                    <option value="">Select opportunity</option>
                    {opportunities.map((opportunity) => (
                      <option key={opportunity.id} value={opportunity.id}>
                        {opportunity.title}
                      </option>
                    ))}
                  </select>
                  <select
                    value={proposalForm.brandId}
                    onChange={(event) => setField(setProposalForm, "brandId", event.target.value)}
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  >
                    <option value="">Select brand</option>
                    {brands.filter((brand) => brand.isActive).map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={proposalForm.clientId}
                    onChange={(event) => setField(setProposalForm, "clientId", event.target.value)}
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  >
                    <option value="">Optional client / contact</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={proposalForm.title}
                    onChange={(event) => setField(setProposalForm, "title", event.target.value)}
                    placeholder="Proposal title"
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <input
                    type="text"
                    value={proposalForm.legalCompanyName}
                    onChange={(event) => setField(setProposalForm, "legalCompanyName", event.target.value)}
                    placeholder="Legal company name"
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  />
                  <input
                    type="text"
                    value={proposalForm.signerName}
                    onChange={(event) => setField(setProposalForm, "signerName", event.target.value)}
                    placeholder="Signer name"
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  />
                  <input
                    type="text"
                    value={proposalForm.signerTitle}
                    onChange={(event) => setField(setProposalForm, "signerTitle", event.target.value)}
                    placeholder="Signer title"
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  />
                  <input
                    type="email"
                    value={proposalForm.signerEmail}
                    onChange={(event) => setField(setProposalForm, "signerEmail", event.target.value)}
                    placeholder="Signer email"
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  />
                  <input
                    type="text"
                    value={proposalForm.signerPhone}
                    onChange={(event) => setField(setProposalForm, "signerPhone", event.target.value)}
                    placeholder="Signer phone"
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  />
                  <input
                    type="text"
                    value={proposalForm.signerAddress}
                    onChange={(event) => setField(setProposalForm, "signerAddress", event.target.value)}
                    placeholder="Signer address"
                    className="rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-stone">Signature date</label>
                    <input type="date" value={proposalForm.signatureDate} onChange={(event) => setField(setProposalForm, "signatureDate", event.target.value)} className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-stone">Effective date</label>
                    <input type="date" value={proposalForm.effectiveDate} onChange={(event) => setField(setProposalForm, "effectiveDate", event.target.value)} className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-stone">Services start</label>
                    <input type="date" value={proposalForm.servicesStartDate} onChange={(event) => setField(setProposalForm, "servicesStartDate", event.target.value)} className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-stone">Billing anchor</label>
                    <input type="date" value={proposalForm.billingAnchorDate} onChange={(event) => setField(setProposalForm, "billingAnchorDate", event.target.value)} className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-stone">Project installment months</label>
                    <input type="number" min="1" max="12" step="1" value={proposalForm.projectInstallmentMonths} onChange={(event) => setField(setProposalForm, "projectInstallmentMonths", event.target.value)} placeholder="Project months" className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-stone">Billing activation</label>
                    <select value={proposalForm.billingActivationMode} onChange={(event) => setProposalForm((current) => ({ ...current, billingActivationMode: event.target.value as "automatic" | "manual" }))} className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss">
                      <option value="automatic">Auto-activate billing</option>
                      <option value="manual">Activate manually after signature</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-stone">Signature provider</label>
                    <input type="text" value={proposalForm.signatureProvider} onChange={(event) => setField(setProposalForm, "signatureProvider", event.target.value)} placeholder="Signature provider" className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-stone">Source document</label>
                    <input type="text" value={proposalForm.sourceDocumentName} onChange={(event) => setField(setProposalForm, "sourceDocumentName", event.target.value)} placeholder="Source document name" className="w-full rounded-xl border border-line bg-white px-4 py-2.5 outline-none focus:border-moss" />
                  </div>
                </div>

                <textarea
                  value={proposalForm.additionalTermsText}
                  onChange={(event) => setField(setProposalForm, "additionalTermsText", event.target.value)}
                  rows={3}
                  placeholder="Optional proposal-specific notes or addendum"
                  className="rounded-xl border border-line bg-white px-4 py-3 outline-none focus:border-moss"
                />

                <div className="rounded-[24px] border border-line bg-slate-50/80 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">Line items</p>
                      <p className="text-xs text-stone">Project items split evenly. Recurring services can start later and continue on subscription.</p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <select
                        value={catalogSelectionId}
                        onChange={(event) => setCatalogSelectionId(event.target.value)}
                        className="rounded-xl border border-line bg-white px-4 py-2 text-sm outline-none focus:border-moss"
                      >
                        <option value="">Add from catalog</option>
                        {availableCatalogItems.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={addCatalogItemToProposal}
                        className="rounded-full border border-line px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-white"
                      >
                        Use item
                      </button>
                      <button
                        type="button"
                        onClick={addLineItem}
                        className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        Add blank line
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {proposalLineItems.map((item, index) => (
                      <div key={`${item.name}-${index}`} className="rounded-2xl border border-line bg-white p-4">
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1 space-y-3">
                            <div className="grid grid-cols-3 gap-3">
                              <select value={item.kind} onChange={(event) => updateLineItem(index, "kind", event.target.value)} className="rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss">
                                <option value="project">Project</option>
                                <option value="service">Service</option>
                              </select>
                              <input type="text" value={item.category || ""} onChange={(event) => updateLineItem(index, "category", event.target.value)} placeholder="Category" className="rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss" />
                              <input type="text" value={item.name} onChange={(event) => updateLineItem(index, "name", event.target.value)} placeholder="Name" className="rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss" />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <input type="number" min="1" step="1" value={item.quantity} onChange={(event) => updateLineItem(index, "quantity", event.target.value)} placeholder="Qty" className="rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss" />
                              <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={(event) => updateLineItem(index, "unitPrice", event.target.value)} placeholder="Unit price" className="rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss" />
                              <input type="number" min="0" step="0.01" value={item.amount} onChange={(event) => updateLineItem(index, "amount", event.target.value)} placeholder="Total" className="rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss" />
                            </div>
                            {item.kind === "service" && (
                              <div className="grid grid-cols-2 gap-3">
                                <input type="number" min="0" step="1" value={item.startDelayMonths} onChange={(event) => updateLineItem(index, "startDelayMonths", event.target.value)} placeholder="Start delay (months)" className="rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss" />
                                <input type="number" min="1" step="1" value={item.durationMonths ?? ""} onChange={(event) => updateLineItem(index, "durationMonths", event.target.value)} placeholder="Duration (blank = recurring)" className="rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss" />
                              </div>
                            )}
                            <textarea value={item.description || ""} onChange={(event) => updateLineItem(index, "description", event.target.value)} rows={2} placeholder="Description" className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss" />
                          </div>
                          <button type="button" onClick={() => removeLineItem(index)} className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-stone transition hover:bg-slate-100 hover:text-rose-600" title="Remove line">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleProposalPreview}
                    disabled={saving}
                    className="rounded-full border border-line bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    Preview schedule
                  </button>
                  <button
                    type="button"
                    onClick={handleProposalCreate}
                    disabled={saving}
                    className="rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss/90 disabled:opacity-50"
                  >
                    {editingProposalId ? "Update proposal" : "Save proposal"}
                  </button>
                  {editingProposalId && (
                    <button type="button" onClick={cancelEditProposal} className="rounded-full border border-line px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-slate-900">Existing proposals</p>
                  <p className="text-sm text-stone">Select one to inspect the frozen schedule and terms snapshot.</p>
                </div>
                <StatusPill tone="neutral" label={`${proposals.length} proposals`} />
              </div>
              <div className="mt-4 space-y-3">
                {proposals.map((proposal) => (
                  <button
                    key={proposal.id}
                    type="button"
                    onClick={() => {
                      setProposalPreview(null)
                      setSelectedProposalId(proposal.id)
                    }}
                    className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                      selectedProposalId === proposal.id && !proposalPreview
                        ? "border-moss bg-moss/5"
                        : "border-line bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{proposal.title}</p>
                        <p className="text-xs text-stone">
                          {proposal.brandName} · {proposal.clientName || "No client linked"}
                        </p>
                      </div>
                      <StatusPill tone={proposalTone(proposal.status)} label={proposal.status} />
                    </div>
                  </button>
                ))}
                {proposals.length === 0 && <p className="text-sm text-stone">No proposals yet.</p>}
              </div>
            </Card>
          </div>

          <div className="min-w-0 space-y-6">
            <Card>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-lg font-semibold text-slate-900">
                    {previewToRender ? "Draft preview" : "Selected proposal"}
                  </p>
                  <p className="text-sm text-stone">
                    {previewToRender
                      ? "This is the schedule the system will use after signature."
                      : "Preview an unsent draft or inspect a saved proposal."}
                  </p>
                </div>
                {previewToRender && <StatusPill tone="warning" label="Preview only" />}
              </div>

              {!previewToRender && selectedProposalId && selectedProposal && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <select
                      value={selectedProposal.clientId || ""}
                      onChange={async (event) => {
                        const newClientId = event.target.value || null
                        setSaving(true)
                        setError(null)
                        try {
                          const response = await fetch(`/api/proposals/${selectedProposalId}`, {
                            method: "PUT",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ clientId: newClientId }),
                          })
                          if (!response.ok) throw new Error("Failed to update client")
                          setSelectedProposal(await fetchProposalDetail(selectedProposalId))
                          await loadWorkspace()
                          setSuccess(newClientId ? "Client linked to proposal." : "Client unlinked from proposal.")
                        } catch {
                          setError("Failed to link client to proposal.")
                        } finally {
                          setSaving(false)
                        }
                      }}
                      className="flex-1 rounded-xl border border-line bg-white px-4 py-2 text-sm outline-none focus:border-moss"
                    >
                      <option value="">No client linked</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}{client.company ? ` (${client.company})` : ""}
                        </option>
                      ))}
                    </select>
                    {!selectedProposal.clientId && (
                      <span className="text-xs font-medium text-amber-600">Link a client to generate documents</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                  {selectedProposal.status === "Draft" && (
                    <button
                      type="button"
                      onClick={() => startEditProposal(selectedProposal)}
                      className="rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss/90"
                    >
                      <span className="inline-flex items-center gap-1.5"><Edit2 className="h-3.5 w-3.5" /> Edit proposal</span>
                    </button>
                  )}
                  <div className={!selectedProposal?.clientId ? "tooltip tooltip-bottom" : ""} data-tip={!selectedProposal?.clientId ? "Link a client to this proposal first" : undefined}>
                    <button
                      type="button"
                      onClick={handleGenerateDocument}
                      disabled={saving || !selectedProposal?.clientId}
                      className="rounded-full border border-line bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      Generate document
                    </button>
                  </div>
                  <a
                    href={`/api/proposals/${selectedProposalId}/export?format=doc`}
                    className="rounded-full border border-line bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Export Word (.doc)
                  </a>
                  <a
                    href={`/api/proposals/${selectedProposalId}/export?format=html`}
                    className="rounded-full border border-line bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Export HTML
                  </a>
                  <button
                    type="button"
                    onClick={handleSendToSignatureProvider}
                    disabled={saving}
                    className="rounded-full border border-line bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    Send to signature provider
                  </button>
                  {selectedProposal?.billingActivationMode === "manual" &&
                    selectedProposal?.status === "Signed" &&
                    selectedProposal?.invoiceScheduleStatus !== "Active" && (
                      <button
                        type="button"
                        onClick={handleActivateBilling}
                        disabled={saving}
                        className="rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss/90 disabled:opacity-50"
                      >
                        Activate billing now
                      </button>
                    )}
                  {selectedProposal?.generatedFileId && (
                    <a
                      href={`/api/files/${selectedProposal.generatedFileId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full border border-line bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Open generated document
                    </a>
                  )}
                  </div>
                </div>
              )}

              {(previewToRender || selectedProposalView) ? (
                <div className="mt-5 space-y-5">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="text-sm font-semibold text-slate-900">
                      {(previewToRender || selectedProposalView)?.title}
                    </p>
                    <p className="mt-1 text-xs text-stone">
                      Terms link: {(previewToRender || selectedProposalView)?.termsUrlSnapshot}
                    </p>
                    <p className="mt-2 text-xs text-stone">
                      Billing activation: {(previewToRender || selectedProposalView)?.billingActivationMode === "manual"
                        ? "Manual after signature"
                        : "Automatic after signature"}
                      {!previewToRender && selectedProposal?.invoiceScheduleStatus
                        ? ` · Schedule ${selectedProposal.invoiceScheduleStatus}`
                        : ""}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-2xl border border-line bg-white p-4">
                      <p className="text-xs uppercase tracking-wider text-stone">Project total</p>
                      <p className="mt-1 text-lg font-semibold text-slate-900">{currency(renderedTotals.project)}</p>
                    </div>
                    <div className="rounded-2xl border border-line bg-white p-4">
                      <p className="text-xs uppercase tracking-wider text-stone">Service total</p>
                      <p className="mt-1 text-lg font-semibold text-slate-900">{currency(renderedTotals.service)}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-slate-900">Invoice schedule</p>
                    {((previewToRender || selectedProposalView)?.invoiceSchedulePreview.length || 0) > 6 ? (
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-stone">
                              <th className="pb-2 pr-3">Period</th>
                              <th className="pb-2 pr-3 text-right">Projects</th>
                              <th className="pb-2 pr-3 text-right">Services</th>
                              <th className="pb-2 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-line">
                            {(previewToRender || selectedProposalView)?.invoiceSchedulePreview.map((row) => (
                              <tr key={`${row.scheduledDate}-${row.monthOffset}`} className={row.isRecurringTail ? "bg-slate-50" : ""}>
                                <td className="py-2 pr-3">
                                  <span className="font-medium text-slate-900">{row.label}</span>
                                  {row.isRecurringTail && <span className="ml-1.5 text-xs text-stone">(subscription)</span>}
                                </td>
                                <td className="py-2 pr-3 text-right text-stone">
                                  {row.projectAmount > 0 ? currency(row.projectAmount) : "\u2014"}
                                </td>
                                <td className="py-2 pr-3 text-right text-stone">
                                  {row.serviceAmount > 0 ? currency(row.serviceAmount) : "\u2014"}
                                </td>
                                <td className="py-2 text-right font-semibold text-slate-900">
                                  {currency(row.totalAmount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="mt-3 space-y-2">
                        {(previewToRender || selectedProposalView)?.invoiceSchedulePreview.map((row) => (
                          <div
                            key={`${row.scheduledDate}-${row.monthOffset}`}
                            className="flex items-center justify-between rounded-2xl border border-line bg-white px-4 py-3 text-sm"
                          >
                            <div>
                              <p className="font-semibold text-slate-900">{row.label}</p>
                              <p className="text-xs text-stone">
                                {row.projectAmount > 0 ? `${currency(row.projectAmount)} project` : "No project"} ·{" "}
                                {row.serviceAmount > 0 ? `${currency(row.serviceAmount)} service` : "No service"}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-slate-900">{currency(row.totalAmount)}</p>
                              {row.isRecurringTail && (
                                <p className="text-xs text-stone">Subscription begins</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-slate-900">General terms snapshot</p>
                    <pre className="mt-3 whitespace-pre-wrap rounded-2xl border border-line bg-slate-50 p-4 text-xs leading-6 text-slate-700">
                      {(previewToRender || selectedProposalView)?.generalTermsSnapshot}
                    </pre>
                  </div>
                </div>
              ) : (
                <p className="mt-5 text-sm text-stone">Choose a saved proposal or generate a preview to inspect the schedule.</p>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}

export default SalesPage
